import type { DataDrivenPropertyValueSpecification, ExpressionSpecification, FilterSpecification } from 'mapbox-gl';
import type { LayerProps } from 'react-map-gl/mapbox';

export const SCREENS_SOURCE_ID = 'mf-screens';
export const VENUES_SOURCE_ID = 'mf-venues';

export const SCREEN_IMAGE_ID = 'mf-screen';
export const VENUE_CLUSTER_ENABLED_IMAGE_ID = 'mf-venue-cluster-enabled';
export const VENUE_CLUSTER_DISABLED_IMAGE_ID = 'mf-venue-cluster-disabled';

export const SCREENS_CLUSTER_LAYER_ID = 'mf-screens-cluster';
export const SCREENS_CLUSTER_ICON_LAYER_ID = 'mf-screens-cluster-icon';
export const SCREENS_CLUSTER_BADGE_LAYER_ID = 'mf-screens-cluster-badge';
export const SCREENS_CLUSTER_COUNT_LAYER_ID = 'mf-screens-cluster-count';
export const SCREEN_BACKGROUND_LAYER_ID = 'mf-screen-bg';
export const SCREEN_ICON_LAYER_ID = 'mf-screen-icon';
export const VENUES_CLUSTER_LAYER_ID = 'mf-venues-cluster';
export const VENUES_CLUSTER_COUNT_LAYER_ID = 'mf-venues-cluster-count';
export const VENUE_GLYPH_LAYER_ID = 'mf-venue-icon';

// Mapbox paint cannot read CSS custom properties, so the design-token values
// are resolved once here. Source: sonata-react-components/src_ts/styles/colors.css
// (--color-extras-purple-400, --color-status-enabled-600/disabled-500/pending-600).
export const SCREEN_CLUSTER_COLOR = '#A265FF';
const SCREEN_MARKER_FILL = 'rgba(245, 238, 255, 0.80)';
export const VENUE_ENABLED_COLOR = '#0C6262';
export const VENUE_DISABLED_COLOR = '#9EA0B8';
const VENUE_PROXIMITY_COLOR = '#2C45D1';
const CLUSTER_COUNT_COLOR = '#FFFFFF';
const CLUSTER_COUNT_FONT = ['DIN Offc Pro Medium', 'Arial Unicode MS Bold'];
const VENUE_GLYPH_FONT = ['Font Awesome 6 Pro Solid'];

// Marker scale buckets reproduced from the removed DOM-marker zoom listener:
// 0.4 below zoom 14, 0.6 at 14, 0.8 at 16, 1.0 at 18. No React state involved.

// Screen single marker: 40px circle (radius 20 x scale) with a 24px icon
// (0.375 of the 64px SDF image x scale), matching the old DOM marker look.
const SCREEN_CIRCLE_RADIUS: DataDrivenPropertyValueSpecification<number> = [
  'step',
  ['zoom'],
  8,
  14,
  12,
  16,
  16,
  18,
  20
];
export const SCREEN_ICON_MAX_SIZE = 0.375;
const SCREEN_ICON_SIZE: DataDrivenPropertyValueSpecification<number> = [
  'step',
  ['zoom'],
  0.15,
  14,
  0.225,
  16,
  0.3,
  18,
  SCREEN_ICON_MAX_SIZE
];

// Venue single marker: 24px glyph x scale, matching the old 1.5rem <i>.
const VENUE_GLYPH_SIZE: DataDrivenPropertyValueSpecification<number> = [
  'step',
  ['zoom'],
  9.6,
  14,
  14.4,
  16,
  19.2,
  18,
  24
];

// Cluster markers are the entity icon (screen / venue pin) with the count inside, sized
// by point_count (0.7 / 0.85 / 1.0 of the 64px image) times the same zoom scale as the
// single markers. Clusters only exist up to zoom 14 (clusterMaxZoom), so only the
// 0.4 (<14) and 0.6 (14) buckets apply. ['zoom'] must be the top-level step input.
const CLUSTER_ZOOM_SCALE_LOW = 0.4;
const CLUSTER_ZOOM_SCALE_HIGH = 0.6;
export const CLUSTER_MAX_ICON_SIZE = CLUSTER_ZOOM_SCALE_HIGH;
// Count text follows the zoom too, kept at a readable minimum.
const CLUSTER_COUNT_SIZE_LOW = 8;
const CLUSTER_COUNT_SIZE_HIGH = 11;

const clusterIconSizeAt = (zoomScale: number): ExpressionSpecification => [
  'step',
  ['get', 'point_count'],
  0.7 * zoomScale,
  100,
  0.85 * zoomScale,
  750,
  zoomScale
];

const CLUSTER_ICON_SIZE: DataDrivenPropertyValueSpecification<number> = [
  'step',
  ['zoom'],
  clusterIconSizeAt(CLUSTER_ZOOM_SCALE_LOW),
  14,
  clusterIconSizeAt(CLUSTER_ZOOM_SCALE_HIGH)
];
const CLUSTER_COUNT_SIZE: DataDrivenPropertyValueSpecification<number> = [
  'step',
  ['zoom'],
  CLUSTER_COUNT_SIZE_LOW,
  14,
  CLUSTER_COUNT_SIZE_HIGH
];

// The venue pin is bottom-anchored; its head centre sits 40px (of 64) above the tip,
// so the count is lifted by 40 x icon-size px, expressed in ems of the count text.
const venueCountOffsetAt = (zoomScale: number, countSize: number): ExpressionSpecification => [
  'step',
  ['get', 'point_count'],
  ['literal', [0, -(40 * 0.7 * zoomScale) / countSize]],
  100,
  ['literal', [0, -(40 * 0.85 * zoomScale) / countSize]],
  750,
  ['literal', [0, -(40 * zoomScale) / countSize]]
];

const VENUE_CLUSTER_COUNT_OFFSET: DataDrivenPropertyValueSpecification<[number, number]> = [
  'step',
  ['zoom'],
  venueCountOffsetAt(CLUSTER_ZOOM_SCALE_LOW, CLUSTER_COUNT_SIZE_LOW),
  14,
  venueCountOffsetAt(CLUSTER_ZOOM_SCALE_HIGH, CLUSTER_COUNT_SIZE_HIGH)
];
// The screen body centre is slightly above the image centre (stand underneath).
// Screen group marker, used for clusters and for stacks (several screens at exactly the
// same coordinates, e.g. a mall): same light circle + purple border + outlined screen icon
// as a single screen (icon = 0.6 of the circle diameter), with the screen count in a
// purple badge on the top-right edge of the circle (45deg, r x 0.707). Clusters only exist
// up to zoom 14 (clusterMaxZoom) but stacks exist at every zoom, so sizes follow the same
// zoom buckets as the single markers (<14, 14, 16, 18): single radius + 2px.
type ZoomBuckets = [
  number | ExpressionSpecification,
  number | ExpressionSpecification,
  number | ExpressionSpecification,
  number | ExpressionSpecification
];
const byZoom = (values: ZoomBuckets): ExpressionSpecification => [
  'step',
  ['zoom'],
  values[0],
  14,
  values[1],
  16,
  values[2],
  18,
  values[3]
];

const SCREEN_GROUP_RADII = [10, 14, 18, 22];
const SCREEN_GROUP_COUNT_SIZES = [8, 10, 10, 11];
const badgeOffset = (bucket: number) => Math.round(SCREEN_GROUP_RADII[bucket] * 0.707);
const mapBuckets = (fn: (bucket: number) => number | ExpressionSpecification): ZoomBuckets => [
  fn(0),
  fn(1),
  fn(2),
  fn(3)
];

// Screens represented by a feature: aggregated per cluster (SCREENS_CLUSTER_PROPERTIES),
// or the stack size of an unclustered feature.
const SCREEN_COUNT: ExpressionSpecification = ['coalesce', ['get', 'screenCount'], ['get', 'count']];
// Same format as Mapbox's point_count_abbreviated: 999, 1.2k, 12k.
const SCREEN_COUNT_ABBREVIATED: ExpressionSpecification = [
  'case',
  ['>=', SCREEN_COUNT, 10000],
  ['concat', ['to-string', ['round', ['/', SCREEN_COUNT, 1000]]], 'k'],
  ['>=', SCREEN_COUNT, 1000],
  ['concat', ['to-string', ['/', ['round', ['/', SCREEN_COUNT, 100]], 10]], 'k'],
  ['to-string', SCREEN_COUNT]
];

const SCREEN_GROUP_RADIUS = byZoom(mapBuckets(bucket => SCREEN_GROUP_RADII[bucket]));
// Same steps as byZoom, for sizing DOM elements over a screen group circle.
export const getScreenGroupRadius = (zoom: number) =>
  SCREEN_GROUP_RADII[[14, 16, 18].filter(step => zoom >= step).length];
// 64px screen image -> 0.6 x diameter.
const SCREEN_GROUP_ICON_SIZE = byZoom(mapBuckets(bucket => (SCREEN_GROUP_RADII[bucket] * 2 * 0.6) / 64));
const SCREEN_GROUP_BADGE_TRANSLATE = byZoom(
  mapBuckets(bucket => ['literal', [badgeOffset(bucket), -badgeOffset(bucket)]])
);
const SCREEN_GROUP_COUNT_SIZE = byZoom(mapBuckets(bucket => SCREEN_GROUP_COUNT_SIZES[bucket]));
// text-offset is in ems of the count text size.
const SCREEN_GROUP_COUNT_OFFSET = byZoom(
  mapBuckets(bucket => {
    const offset = badgeOffset(bucket) / SCREEN_GROUP_COUNT_SIZES[bucket];
    return ['literal', [offset, -offset]];
  })
);
// Badge radius by number of digits (the abbreviated count is at most 4 chars, e.g. "1.2k").
const badgeRadiusAt = (radii: [number, number, number, number]): ExpressionSpecification => [
  'step',
  SCREEN_COUNT,
  radii[0],
  10,
  radii[1],
  100,
  radii[2],
  1000,
  radii[3]
];
const SCREEN_GROUP_BADGE_RADIUS = byZoom([
  badgeRadiusAt([6, 7, 8.5, 10]),
  badgeRadiusAt([7, 8, 10, 12]),
  badgeRadiusAt([7, 8, 10, 12]),
  badgeRadiusAt([8, 9, 11, 13])
]);

export const SCREENS_CLUSTER_PROPERTIES = {
  screenCount: ['+', ['get', 'count']]
};

// enabledCount is aggregated per cluster (see VENUES_CLUSTER_PROPERTIES): a cluster is
// enabled if at least one venue in it is not disabled, disabled only if all are.
const VENUE_CLUSTER_IMAGE: DataDrivenPropertyValueSpecification<string> = [
  'case',
  ['>', ['get', 'enabledCount'], 0],
  VENUE_CLUSTER_ENABLED_IMAGE_ID,
  VENUE_CLUSTER_DISABLED_IMAGE_ID
];

export const VENUES_CLUSTER_PROPERTIES = {
  enabledCount: ['+', ['case', ['==', ['get', 'attributionState'], 'disabled'], 0, 1]]
};

const CLUSTER_FILTER: FilterSpecification = ['has', 'point_count'];
const SINGLE_FILTER: FilterSpecification = ['!', ['has', 'point_count']];
// Screens: a stack (count > 1) is drawn like a cluster; ['has', 'point_count'] goes first so
// cluster features (no count property) never evaluate the count comparison.
const SCREEN_GROUP_FILTER: FilterSpecification = ['any', ['has', 'point_count'], ['>', ['get', 'count'], 1]];
const SCREEN_SINGLE_FILTER: FilterSpecification = ['all', ['!', ['has', 'point_count']], ['==', ['get', 'count'], 1]];

export const screensClusterLayer: LayerProps = {
  filter: SCREEN_GROUP_FILTER,
  id: SCREENS_CLUSTER_LAYER_ID,
  paint: {
    'circle-color': SCREEN_MARKER_FILL,
    'circle-radius': SCREEN_GROUP_RADIUS,
    'circle-stroke-color': SCREEN_CLUSTER_COLOR,
    'circle-stroke-width': 2
  },
  source: SCREENS_SOURCE_ID,
  type: 'circle'
};

export const screensClusterIconLayer: LayerProps = {
  filter: SCREEN_GROUP_FILTER,
  id: SCREENS_CLUSTER_ICON_LAYER_ID,
  layout: {
    'icon-allow-overlap': true,
    'icon-image': SCREEN_IMAGE_ID,
    'icon-size': SCREEN_GROUP_ICON_SIZE
  },
  source: SCREENS_SOURCE_ID,
  type: 'symbol'
};

export const screensClusterBadgeLayer: LayerProps = {
  filter: SCREEN_GROUP_FILTER,
  id: SCREENS_CLUSTER_BADGE_LAYER_ID,
  paint: {
    'circle-color': SCREEN_CLUSTER_COLOR,
    'circle-radius': SCREEN_GROUP_BADGE_RADIUS,
    'circle-translate': SCREEN_GROUP_BADGE_TRANSLATE
  },
  source: SCREENS_SOURCE_ID,
  type: 'circle'
};

export const screensClusterCountLayer: LayerProps = {
  filter: SCREEN_GROUP_FILTER,
  id: SCREENS_CLUSTER_COUNT_LAYER_ID,
  layout: {
    'text-allow-overlap': true,
    'text-field': SCREEN_COUNT_ABBREVIATED,
    'text-font': CLUSTER_COUNT_FONT,
    'text-offset': SCREEN_GROUP_COUNT_OFFSET,
    'text-size': SCREEN_GROUP_COUNT_SIZE
  },
  paint: {
    'text-color': CLUSTER_COUNT_COLOR
  },
  source: SCREENS_SOURCE_ID,
  type: 'symbol'
};

export const screenBackgroundLayer: LayerProps = {
  filter: SCREEN_SINGLE_FILTER,
  id: SCREEN_BACKGROUND_LAYER_ID,
  paint: {
    'circle-color': SCREEN_MARKER_FILL,
    'circle-radius': SCREEN_CIRCLE_RADIUS,
    'circle-stroke-color': SCREEN_CLUSTER_COLOR,
    'circle-stroke-width': 2
  },
  source: SCREENS_SOURCE_ID,
  type: 'circle'
};

export const screenIconLayer: LayerProps = {
  filter: SCREEN_SINGLE_FILTER,
  id: SCREEN_ICON_LAYER_ID,
  layout: {
    'icon-allow-overlap': true,
    'icon-image': SCREEN_IMAGE_ID,
    'icon-size': SCREEN_ICON_SIZE
  },
  source: SCREENS_SOURCE_ID,
  type: 'symbol'
};

export const venuesClusterLayer: LayerProps = {
  filter: CLUSTER_FILTER,
  id: VENUES_CLUSTER_LAYER_ID,
  layout: {
    'icon-allow-overlap': true,
    'icon-anchor': 'bottom',
    'icon-image': VENUE_CLUSTER_IMAGE,
    'icon-size': CLUSTER_ICON_SIZE
  },
  source: VENUES_SOURCE_ID,
  type: 'symbol'
};

export const venuesClusterCountLayer: LayerProps = {
  filter: CLUSTER_FILTER,
  id: VENUES_CLUSTER_COUNT_LAYER_ID,
  layout: {
    'text-allow-overlap': true,
    'text-field': ['get', 'point_count_abbreviated'],
    'text-font': CLUSTER_COUNT_FONT,
    'text-offset': VENUE_CLUSTER_COUNT_OFFSET,
    'text-size': CLUSTER_COUNT_SIZE
  },
  paint: {
    'text-color': CLUSTER_COUNT_COLOR
  },
  source: VENUES_SOURCE_ID,
  type: 'symbol'
};

export const venueGlyphLayer: LayerProps = {
  filter: SINGLE_FILTER,
  id: VENUE_GLYPH_LAYER_ID,
  layout: {
    'text-allow-overlap': true,
    'text-anchor': 'bottom',
    'text-field': ['match', ['get', 'attributionState'], 'proximity', '\uf08d', '\uf3c5'],
    'text-font': VENUE_GLYPH_FONT,
    'text-size': VENUE_GLYPH_SIZE
  },
  paint: {
    'text-color': [
      'match',
      ['get', 'attributionState'],
      'enabled',
      VENUE_ENABLED_COLOR,
      'proximity',
      VENUE_PROXIMITY_COLOR,
      VENUE_DISABLED_COLOR
    ]
  },
  source: VENUES_SOURCE_ID,
  type: 'symbol'
};

export const INTERACTIVE_LAYER_IDS = [
  SCREENS_CLUSTER_LAYER_ID,
  SCREENS_CLUSTER_BADGE_LAYER_ID,
  SCREEN_BACKGROUND_LAYER_ID,
  SCREEN_ICON_LAYER_ID,
  VENUES_CLUSTER_LAYER_ID,
  VENUE_GLYPH_LAYER_ID
];

export const CLUSTER_LAYER_IDS = [SCREENS_CLUSTER_LAYER_ID, VENUES_CLUSTER_LAYER_ID];
